import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ViolationsList = ({ violations, onUpdateStatus, loading }) => {
  const getSeverityColor = (severity) => {
    const colors = {
      critical: 'text-error',
      high: 'text-warning',
      medium: 'text-primary',
      low: 'text-muted-foreground'
    };
    return colors?.[severity] || 'text-muted-foreground';
  };

  const getSeverityBg = (severity) => {
    const backgrounds = {
      critical: 'bg-error/10',
      high: 'bg-warning/10',
      medium: 'bg-primary/10',
      low: 'bg-muted'
    };
    return backgrounds?.[severity] || 'bg-muted';
  };

  const getStatusColor = (status) => {
    const colors = {
      identified: 'text-muted-foreground',
      pending_review: 'text-warning',
      approved: 'text-info',
      in_progress: 'text-primary',
      completed: 'text-success',
      rejected: 'text-error'
    };
    return colors?.[status] || 'text-muted-foreground';
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'K0.00';
    return `K${amount?.toFixed(2)?.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString)?.toLocaleDateString('en-PG', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icon name="Loader2" size={48} color="var(--color-primary)" className="animate-spin" />
      </div>
    );
  }

  if (!violations || violations?.length === 0) {
    return (
      <div className="py-12 text-center">
        <Icon name="CheckCircle" size={48} color="var(--color-success)" className="mx-auto mb-3" />
        <p className="caption text-sm text-success font-medium">No violations found</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {violations?.map((violation) => (
        <div key={violation?.id} className="border border-border rounded-lg p-4 hover:shadow-sm transition-smooth">
          <div className="flex items-start justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Icon
                name="AlertTriangle"
                size={18}
                color={getSeverityColor(violation?.severity)?.replace('text-', 'var(--color-)')}
              />
              <h4 className="font-heading font-semibold text-sm text-foreground capitalize">
                {violation?.violationType?.replace(/_/g, ' ')}
              </h4>
            </div>
            <div className="flex items-center gap-2">
              <span className={`px-2 py-1 ${getSeverityBg(violation?.severity)} ${getSeverityColor(violation?.severity)} text-xs font-caption rounded-full capitalize`}>
                {violation?.severity}
              </span>
              <span className={`px-2 py-1 bg-muted ${getStatusColor(violation?.violationStatus)} text-xs font-caption rounded-full capitalize`}>
                {violation?.violationStatus?.replace(/_/g, ' ')}
              </span>
            </div>
          </div>

          <p className="caption text-sm text-muted-foreground mb-3">{violation?.description}</p>

          {violation?.regulationReference && (
            <div className="flex items-center gap-2 mb-2">
              <Icon name="BookOpen" size={14} color="var(--color-primary)" />
              <p className="caption text-xs text-primary">
                <strong>Regulation:</strong> {violation?.regulationReference}
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Affected</p>
              <p className="font-heading font-medium text-sm text-foreground">
                {violation?.affectedCount} employees
              </p>
            </div>
            {violation?.department && (
              <div>
                <p className="caption text-xs text-muted-foreground mb-1">Department</p>
                <p className="font-heading font-medium text-sm text-foreground">
                  {violation?.department}
                </p>
              </div>
            )}
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Estimated Cost</p>
              <p className="font-heading font-medium text-sm text-warning">
                {formatCurrency(violation?.estimatedCost)}
              </p>
            </div>
            <div>
              <p className="caption text-xs text-muted-foreground mb-1">Identified</p>
              <p className="caption text-xs text-foreground">
                {formatDate(violation?.identifiedAt)}
              </p>
            </div>
          </div>

          {violation?.correctiveAction && (
            <div className="p-3 bg-primary/5 border-l-4 border-primary rounded mb-3">
              <p className="caption text-xs font-medium text-foreground mb-1">Corrective Action</p>
              <p className="caption text-xs text-muted-foreground">{violation?.correctiveAction}</p>
            </div>
          )}

          <div className="flex items-center gap-2">
            {violation?.violationStatus === 'identified' && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onUpdateStatus?.(violation?.id, 'pending_review')}
              >
                Mark for Review
              </Button>
            )}
            {violation?.violationStatus === 'pending_review' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onUpdateStatus?.(violation?.id, 'approved')}
              >
                Approve
              </Button>
            )}
            {violation?.violationStatus === 'approved' && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onUpdateStatus?.(violation?.id, 'in_progress')}
              >
                Start Remediation
              </Button>
            )}
            {violation?.violationStatus === 'in_progress' && (
              <Button
                variant="success"
                size="sm"
                onClick={() => onUpdateStatus?.(violation?.id, 'completed')}
              >
                Mark Complete
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ViolationsList;